import { AxiosResponse } from 'axios';
import isEmpty from 'lodash/isEmpty';

import { messages } from '@qality/common-messages';
import {
  TestCaseTestSteps,
  TestStep,
  TestStepsCollection,
} from '@qality/common-model';
import TestCase from '@qality/common-model/src/TestCase';
import {
  StorageOfUniqueServices,
  AxiosWrapperService as axios,
} from '@qality/common-service';

export default {
  async createTestStep(testStep: TestStep): Promise<AxiosResponse<TestStep>> {
    try {
      const url = await StorageOfUniqueServices.get(
        'AuthenticateService'
      ).authenticateByJWT(
        StorageOfUniqueServices.get('WindowOriginService').origin +
          StorageOfUniqueServices.get('APIConstants').URL_TEST_STEP
      );
      const requestData = TestStep.getCreateRequestData(testStep);
      return await axios.post(url, requestData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.testCaseBox.saveTestStep.message']
      );
    }
  },

  async createNewTestStepIfNeeded(
    testStep: TestStep,
    testCaseIssueId: string | number
  ): Promise<TestStep> {
    if (testStep.isNew) {
      if (testStep.attachmentsTemp?.length) {
        const attachments = await StorageOfUniqueServices.get(
          'AttachmentService'
        ).postAttachments(testCaseIssueId, testStep.attachmentsTemp);
        testStep.attachments = attachments;
      }
      testStep.origin = this.createTestStep(testStep);
      testStep.isNew = false;
    }
    return testStep;
  },

  async fetchTestSteps(issueId: number | string): Promise<TestStepsCollection> {
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_CASES +
        `/${issueId}/testSteps`
    );
    const response = await axios.get(url);
    if (isEmpty(response.data)) {
      return new TestStepsCollection();
    }

    const testSteps = response.data.map((testStep) => {
      return TestStep.from(testStep);
    });
    return new TestStepsCollection(testSteps);
  },

  async bulkFetchTestSteps(
    testCaseIds: Array<number | string>
  ): Promise<TestCaseTestSteps[]> {
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_BULK_TEST_STEP
    );

    const response = await axios.post(
      url,
      { testCaseIds: testCaseIds },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    if (isEmpty(response.data)) {
      return [];
    }
    return response.data;
  },

  async deleteTestStep(testStep: TestStep): Promise<AxiosResponse> {
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_CASES +
        `/${testStep.testCaseId}/testSteps/${await TestStep.getId(testStep)}`
    );
    return await axios.delete(url);
  },

  async updateTestStep(testStep: TestStep): Promise<TestStep> {
    const requestData = await TestStep.getUpdateRequestData(testStep);
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_CASES +
        `/${testStep.testCaseId}/testSteps/${await TestStep.getId(testStep)}`
    );
    const response = await axios.patch(url, requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  async testStepUpdatePositions(
    testCaseId: number,
    testSteps: Array<TestStep>
  ): Promise<void> {
    const testStepsPayload = await Promise.all(
      testSteps
        .filter((testStep) => !testStep.isNew)
        .map(async (testStep, index) => {
          const id = await TestStep.getId(testStep);
          return { id: id, fields: { position: index } };
        })
    );
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_CASES +
        '/' +
        testCaseId +
        '/testSteps/bulk'
    );
    await axios.patch(url, testStepsPayload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },

  async convertOriginPromisesToObjects(
    testCase: TestCase
  ): Promise<Array<TestStep>> {
    let testSteps = testCase.testSteps.testSteps;

    testSteps = await Promise.all(
      testSteps.map(async (testStep) => {
        if (testStep.origin) {
          testStep.origin = (await testStep.origin) ?? {};
        }
        return testStep;
      })
    );

    return testSteps;
  },

  convertOriginObjectsToPromises(testCase: TestCase): Array<TestStep> {
    const testSteps = testCase.testSteps.testSteps;

    return testSteps.map((testStep) => {
      if (testStep.origin) {
        // TODO:
        // @ts-expect-error <no idea what the problem is>
        testStep.origin = Promise.resolve(testStep.origin);
      }
      return testStep;
    });
  },

  async getTestStepsForIssue(issueId: number): Promise<TestStepsCollection> {
    const testSteps = await this.fetchTestSteps(issueId);
    if (testSteps.testSteps.length > 0) {
      return testSteps;
    }

    const newStep = new TestStep(issueId);
    return new TestStepsCollection([newStep]);
  },
};
